import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navbar.css';
import MegaMenu from './MegaMenu';
import { X, ChevronDown, ArrowRight, MapPin, Phone, Mail } from 'lucide-react';

export const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDesktopMenu, setActiveDesktopMenu] = useState<'services' | 'solutions' | 'products' | 'industries' | null>(null);
  
  // Mobile accordion state
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);

  const location = useLocation();
  const currentPath = location.pathname;

  const menuTimeoutRef = React.useRef<any>(null);

  // Monitor scroll to adjust styling/scrolled states
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
    };
  }, []);

  const handleMouseEnter = (menu: 'services' | 'solutions' | 'products' | 'industries') => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
    setActiveDesktopMenu(menu);
  };

  const handleMouseLeave = () => {
    if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
    menuTimeoutRef.current = setTimeout(() => {
      setActiveDesktopMenu(null);
    }, 150);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
    setMobileSolutionsOpen(false);
    setMobileProductsOpen(false);
  };

  return (
    <>
      <header className={`navbar-header-floating ${scrolled ? 'navbar-scrolled' : ''}`}>
        
        {/* Topbar - hidden on mobile and when scrolled */}
        <div className="navbar-topbar">
          <div className="container topbar-container">
            <div className="topbar-left">
              <a
                href="https://www.google.com/maps/place/Vivify+Soft+Tech/@13.0958613,80.17367,17z/data=!3m1!4b1!4m6!3m5!1s0x3a5263fd873ad447:0x5966f5106b73fc96!8m2!3d13.0958613!4d80.17367!16s%2Fg%2F11w2_k9f7b"
                target="_blank"
                rel="noopener noreferrer"
                className="topbar-item topbar-link"
                title="View Vivify Soft Tech location on Google Maps"
              >
                <MapPin size={14} className="topbar-icon" />
                <span>Mugappair Road, Padi, Chennai, Tamil Nadu 600058</span>
              </a>
              <div className="topbar-divider"></div>
              <div className="topbar-item">
                <Phone size={14} className="topbar-icon" />
                <span>+91 7305 7305 28</span>
              </div>
              <div className="topbar-divider"></div>
              <div className="topbar-item">
                <Mail size={14} className="topbar-icon" />
                <span>reach@vivifysoft.com</span>
              </div>
            </div>
            <div className="topbar-right">
              <a href="https://www.linkedin.com/company/vivify-soft-tech/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="topbar-social">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
              </a>
              <a href="https://www.instagram.com/vivify_soft_tech/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="topbar-social">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
              </a>
              <a href="https://www.facebook.com/profile.php?id=61589751765485" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="topbar-social">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
              </a>
            </div>
          </div>
        </div>

        <div className="navbar-container-floating container">
          {/* Logo Area (Sits outside pill, left) */}
          <Link to="/" className="navbar-logo-floating" onClick={closeMobileMenu}>
            <img 
              src="/assets/images/vivify-soft-tech-logo.webp" 
              srcSet="/assets/images/vivify-soft-tech-logo-800w.webp 800w, /assets/images/vivify-soft-tech-logo.webp 1200w"
              sizes="(max-width: 768px) 150px, 200px"
              alt="Vivify Soft Tech - Custom Software & AI Development" 
              className="logo-main-img" 
              title="Vivify Soft Tech" 
            />
          </Link>

          {/* Desktop Navigation Glass Pill */}
          <nav className="navbar-desktop-nav-pill">
            <ul className="nav-links-pill">
              <li>
                <Link 
                  to="/" 
                  className={`nav-link-item-pill ${currentPath === '/' ? 'active' : ''}`}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link 
                  to="/about" 
                  className={`nav-link-item-pill ${currentPath === '/about' ? 'active' : ''}`}
                >
                  About
                </Link>
              </li>
              
              {/* Services Dropdown */}
              <li 
                className="dropdown-trigger-pill-li"
                onMouseEnter={() => handleMouseEnter('services')}
                onMouseLeave={handleMouseLeave}
              >
                <Link 
                  to="/services" 
                  className={`nav-link-item-pill ${currentPath === '/services' || currentPath.startsWith('/services/') || activeDesktopMenu === 'services' ? 'active' : ''}`}
                  onClick={(e) => {
                    if (activeDesktopMenu !== 'services') {
                      e.preventDefault();
                      setActiveDesktopMenu('services');
                    }
                  }}
                >
                  Services <ChevronDown size={12} className="chevron-pill" />
                </Link>
                <div onMouseEnter={() => handleMouseEnter('services')} onMouseLeave={handleMouseLeave}>
                  <MegaMenu 
                    type="services" 
                    isOpen={activeDesktopMenu === 'services'} 
                    onClose={() => setActiveDesktopMenu(null)} 
                  />
                </div>
              </li>

              {/* Solutions Dropdown */}
              <li 
                className="dropdown-trigger-pill-li"
                onMouseEnter={() => handleMouseEnter('solutions')}
                onMouseLeave={handleMouseLeave}
              >
                <Link 
                  to="/solutions" 
                  className={`nav-link-item-pill ${currentPath === '/solutions' || currentPath.startsWith('/solutions/') || activeDesktopMenu === 'solutions' ? 'active' : ''}`}
                  onClick={(e) => {
                    if (activeDesktopMenu !== 'solutions') {
                      e.preventDefault();
                      setActiveDesktopMenu('solutions');
                    }
                  }}
                >
                  Solutions <ChevronDown size={12} className="chevron-pill" />
                </Link>
                <div onMouseEnter={() => handleMouseEnter('solutions')} onMouseLeave={handleMouseLeave}>
                  <MegaMenu 
                    type="solutions" 
                    isOpen={activeDesktopMenu === 'solutions'} 
                    onClose={() => setActiveDesktopMenu(null)} 
                  />
                </div>
              </li>

              {/* Products Dropdown */}
              <li 
                className="dropdown-trigger-pill-li"
                onMouseEnter={() => handleMouseEnter('products')}
                onMouseLeave={handleMouseLeave}
              >
                <Link 
                  to="/products" 
                  className={`nav-link-item-pill ${currentPath === '/products' || currentPath.startsWith('/products/') || activeDesktopMenu === 'products' ? 'active' : ''}`}
                  onClick={(e) => {
                    if (activeDesktopMenu !== 'products') {
                      e.preventDefault();
                      setActiveDesktopMenu('products');
                    }
                  }}
                >
                  Products <ChevronDown size={12} className="chevron-pill" />
                </Link>
                <div onMouseEnter={() => handleMouseEnter('products')} onMouseLeave={handleMouseLeave}>
                  <MegaMenu 
                    type="products" 
                    isOpen={activeDesktopMenu === 'products'} 
                    onClose={() => setActiveDesktopMenu(null)} 
                  />
                </div>
              </li>

              {/* Industries - Regular Link */}
              <li>
                <Link 
                  to="/industries" 
                  className={`nav-link-item-pill ${currentPath === '/industries' || currentPath.startsWith('/industries/') ? 'active' : ''}`}
                >
                  Industries
                </Link>
              </li>

              <li>
                <Link 
                  to="/case-studies" 
                  className={`nav-link-item-pill ${currentPath === '/case-studies' || currentPath.startsWith('/case-studies/') ? 'active' : ''}`}
                >
                  Case Studies
                </Link>
              </li>
              <li>
                <Link 
                  to="/insights" 
                  className={`nav-link-item-pill ${currentPath === '/insights' || currentPath.startsWith('/insights/') ? 'active' : ''}`}
                >
                  Insights
                </Link>
              </li>
              <li>
                <Link 
                  to="/careers" 
                  className={`nav-link-item-pill ${currentPath === '/careers' ? 'active' : ''}`}
                >
                  Careers
                </Link>
              </li>
            </ul>
          </nav>

          {/* Action Area (CTA sits outside pill, right) */}
          <div className="navbar-actions-floating">
            <Link to="/contact" className="cta-pill-btn">
              Contact Us <ArrowRight size={14} className="cta-arrow" />
            </Link>

            {/* Hamburger Button (visible on tablet/mobile) */}
            <button 
              className="mobile-hamburger-pill-btn" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? (
                <X size={24} color="#ffffff" />
              ) : (
                <div className="hamburger-icon">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Slide-Over Drawer */}
      <div className={`mobile-menu-drawer ${mobileMenuOpen ? 'drawer-open' : ''}`}>
        <div className="drawer-container-pill">
          <ul className="mobile-nav-links-pill">
            <li><Link to="/" className={`mobile-nav-item-pill ${currentPath === '/' ? 'active' : ''}`} onClick={closeMobileMenu}>Home</Link></li>
            <li><Link to="/about" className={`mobile-nav-item-pill ${currentPath === '/about' ? 'active' : ''}`} onClick={closeMobileMenu}>About Us</Link></li>
            
            {/* Services Accordion */}
            <li>
              <div className="mobile-nav-accordion-row">
                <Link
                  to="/services"
                  className={`mobile-nav-accordion-label ${currentPath === '/services' ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  Services
                </Link>
                <button
                  className="mobile-nav-accordion-chevron"
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  aria-label="Toggle Services submenu"
                >
                  <ChevronDown size={14} className={`arrow ${mobileServicesOpen ? 'rotated' : ''}`} />
                </button>
              </div>
              <div className={`mobile-submenu ${mobileServicesOpen ? 'submenu-open' : ''}`}>
                <Link to="/services/enterprise-software-solutions" onClick={closeMobileMenu}>Enterprise Software Solutions</Link>
                <Link to="/services/industrial-software-automation" onClick={closeMobileMenu}>Industrial Automation Service</Link>
                <Link to="/services/custom-product-development" onClick={closeMobileMenu}>Custom Product Development</Link>
                <Link to="/services/cloud-it-services" onClick={closeMobileMenu}>Cloud & IT Services</Link>
              </div>
            </li>

            {/* Solutions Accordion */}
            <li>
              <div className="mobile-nav-accordion-row">
                <Link
                  to="/solutions"
                  className={`mobile-nav-accordion-label ${currentPath === '/solutions' ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  Solutions
                </Link>
                <button
                  className="mobile-nav-accordion-chevron"
                  onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}
                  aria-label="Toggle Solutions submenu"
                >
                  <ChevronDown size={14} className={`arrow ${mobileSolutionsOpen ? 'rotated' : ''}`} />
                </button>
              </div>
              <div className={`mobile-submenu ${mobileSolutionsOpen ? 'submenu-open' : ''}`}>
                <Link to="/solutions/ai-business-solutions" onClick={closeMobileMenu}>AI Business Solutions</Link>
                <Link to="/solutions/enterprise-digital-transformation" onClick={closeMobileMenu}>Enterprise Digital Transformation</Link>
                <Link to="/solutions/intelligent-automation-systems" onClick={closeMobileMenu}>Intelligent Automation Systems</Link>
                <Link to="/solutions/industrial-iot-solutions" onClick={closeMobileMenu}>Industrial IoT Solutions</Link>
                <Link to="/solutions/enterprise-workflow-platforms" onClick={closeMobileMenu}>Enterprise Workflow Platforms</Link>
                <Link to="/solutions/business-process-automation" onClick={closeMobileMenu}>Business Process Automation</Link>
                <Link to="/solutions/cloud-based-systems" onClick={closeMobileMenu}>Cloud-Based Systems</Link>
                <Link to="/solutions/scalable-enterprise-applications" onClick={closeMobileMenu}>Scalable Enterprise Applications</Link>
              </div>
            </li>

            {/* Products Accordion */}
            <li>
              <div className="mobile-nav-accordion-row">
                <Link
                  to="/products"
                  className={`mobile-nav-accordion-label ${currentPath.startsWith('/products') ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  Products
                </Link>
                <button
                  className="mobile-nav-accordion-chevron"
                  onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                  aria-label="Toggle Products submenu"
                >
                  <ChevronDown size={14} className={`arrow ${mobileProductsOpen ? 'rotated' : ''}`} />
                </button>
              </div>
              <div className={`mobile-submenu ${mobileProductsOpen ? 'submenu-open' : ''}`}>
                <Link to="/products/securai" onClick={closeMobileMenu}>SecurAI</Link>
                <Link to="/products/factorai" onClick={closeMobileMenu}>FactorAI</Link>
                <Link to="/products/smilai" onClick={closeMobileMenu}>SmilAI</Link>
              </div>
            </li>

            <li><Link to="/industries" className={`mobile-nav-item-pill ${currentPath === '/industries' ? 'active' : ''}`} onClick={closeMobileMenu}>Industries</Link></li>

            <li><Link to="/case-studies" className={`mobile-nav-item-pill ${currentPath === '/case-studies' ? 'active' : ''}`} onClick={closeMobileMenu}>Case Studies</Link></li>
            <li><Link to="/insights" className={`mobile-nav-item-pill ${currentPath === '/insights' ? 'active' : ''}`} onClick={closeMobileMenu}>Insights</Link></li>
            <li><Link to="/careers" className={`mobile-nav-item-pill ${currentPath === '/careers' ? 'active' : ''}`} onClick={closeMobileMenu}>Careers</Link></li>
            <li>
              <Link 
                to="/contact"
                onClick={closeMobileMenu}
                className="mobile-nav-item-pill mobile-drawer-contact-link"
              >
                Contact Us <ArrowRight size={14} className="cta-arrow" style={{ marginLeft: '0.4rem' }} />
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};
export default Navbar;
